import { AUTO_STRATEGY_BID_STEP_IN_CENTS } from 'utils/constants/auction';

import { BiddingStrategy, BidFrequencyStatus } from 'store/models/auctionData';
import {
    AuctionCreateFieldKey,
    AuctionsToCreate,
    VacanciesAuctionInfo,
    Vacancy,
    VacancyBidsRatings,
} from 'store/models/multiCreation';

type ChangeAllFieldsProps = (
    auctionsToCreate: AuctionsToCreate,
    fieldKey: AuctionCreateFieldKey,
    newValue: string | null
) => AuctionsToCreate;

export const changeAllFields: ChangeAllFieldsProps = (auctionsToCreate, fieldKey, newValue) => {
    const newAuctionsToCreate: AuctionsToCreate = {};

    Object.keys(auctionsToCreate).forEach((key) => {
        const vacancyId = Number(key);
        newAuctionsToCreate[vacancyId] = { ...auctionsToCreate[vacancyId], [fieldKey]: newValue };
    });

    return newAuctionsToCreate;
};

type ChangeBidFrequencyStatusProps = (
    auctionsToCreate: AuctionsToCreate,
    bidStatuses?: VacancyBidsRatings[]
) => AuctionsToCreate;

export const changeBidFrequencyStatus: ChangeBidFrequencyStatusProps = (auctionsToCreate, bidStatuses) => {
    const newAuctionsToCreate: AuctionsToCreate = {};
    const bidStatusMap = new Map<number, BidFrequencyStatus | null>();
    if (bidStatuses) {
        bidStatuses.forEach((bidStatusItem) => {
            bidStatusMap.set(bidStatusItem.vacancyId, bidStatusItem.bidFrequencyStatus);
        });
    }

    Object.keys(auctionsToCreate).forEach((key) => {
        const vacancyId = Number(key);
        const bidFrequencyStatus = bidStatusMap.get(vacancyId) ?? null;

        newAuctionsToCreate[vacancyId] = {
            ...auctionsToCreate[vacancyId],
            bidFrequencyStatus,
        };
    });

    return newAuctionsToCreate;
};

type ChangeAllFieldsToRecommended = (
    auctionsToCreate: AuctionsToCreate,
    vacanciesAuctionInfo: VacanciesAuctionInfo | null,
    biddingStrategy?: BiddingStrategy
) => AuctionsToCreate;

export const changeAllBidsToRecommended: ChangeAllFieldsToRecommended = (
    auctionsToCreate,
    vacanciesAuctionInfo,
    biddingStrategy
) => {
    const newAuctionsToCreate: AuctionsToCreate = {};

    Object.keys(auctionsToCreate).forEach((key) => {
        const vacancyId = Number(key);
        newAuctionsToCreate[vacancyId] = {
            ...auctionsToCreate[vacancyId],
        };
        if (!vacanciesAuctionInfo?.[vacancyId]) {
            return;
        }
        const { bidRating, bid } = vacanciesAuctionInfo[vacancyId];

        const additionalAutoStrategyBid =
            biddingStrategy === BiddingStrategy.Auto ? AUTO_STRATEGY_BID_STEP_IN_CENTS : 0;
        const minValue = bid.minValue + additionalAutoStrategyBid;
        const oftenThresholdBid = bidRating.oftenThresholdBid < minValue ? minValue : bidRating.oftenThresholdBid;

        newAuctionsToCreate[vacancyId].bid = String(oftenThresholdBid);
        newAuctionsToCreate[vacancyId].minBidValue = minValue;
        newAuctionsToCreate[vacancyId].bidFrequencyStatus = BidFrequencyStatus.Often;
    });

    return newAuctionsToCreate;
};

export const changeAllBudgetsToRecommended: ChangeAllFieldsToRecommended = (auctionsToCreate, vacanciesAuctionInfo) => {
    const newAuctionsToCreate: AuctionsToCreate = {};

    Object.keys(auctionsToCreate).forEach((key) => {
        const vacancyId = Number(key);
        newAuctionsToCreate[vacancyId] = {
            ...auctionsToCreate[vacancyId],
            ...(vacanciesAuctionInfo
                ? {
                      budget: String(vacanciesAuctionInfo[vacancyId].bidRating.recommendedBudget),
                  }
                : {}),
        };
    });

    return newAuctionsToCreate;
};

type DeleteMultiCreationItemProps = (
    auctionsToCreate: AuctionsToCreate,
    vacanciesAuctionInfo: VacanciesAuctionInfo | null,
    vacancyId: number
) => AuctionsToCreate;

export const deleteMultiCreationItem: DeleteMultiCreationItemProps = (
    auctionsToCreate,
    vacanciesAuctionInfo,
    vacancyId
) => {
    const newAuctionsToCreate = { ...auctionsToCreate };
    const newVacanciesAuctionInfo = { ...vacanciesAuctionInfo };

    delete newAuctionsToCreate[vacancyId];
    delete newVacanciesAuctionInfo[vacancyId];

    return { auctionsToCreate: newAuctionsToCreate, vacanciesAuctionInfo: newVacanciesAuctionInfo };
};

const sortVacanciesById = (a: Vacancy, b: Vacancy) => (a.vacancyId > b.vacancyId ? 1 : -1);

export const sortVacanciesBySelectedAndAdded = (
    vacancies: Vacancy[],
    selectedVacancyIds: Record<number, boolean>,
    vacanciesAuctionInfo: VacanciesAuctionInfo | null
): Vacancy[] => {
    const selected: Vacancy[] = [];
    const added: Vacancy[] = [];
    const middle: Vacancy[] = [];

    vacancies.forEach((vacancy) => {
        if (selectedVacancyIds[vacancy.vacancyId]) {
            selected.push(vacancy);
            return;
        }
        if (vacanciesAuctionInfo && vacanciesAuctionInfo[vacancy.vacancyId]) {
            added.push(vacancy);
            return;
        }
        middle.push(vacancy);
    });

    return [...selected.sort(sortVacanciesById), ...middle.sort(sortVacanciesById), ...added.sort(sortVacanciesById)];
};

export default {};
